import { MemberBreakSettingsQuery, MemberBreakSettingsQueryVariables } from '__generated__/graphql';
import gql from 'graphql-tag';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import { useBreak } from 'hooks/models';
import { IEntryAndLog } from 'hooks/models/time-entry/useTimeEntry';
import useTimeEntryQuery from 'hooks/models/time-entry/useTimeEntryQuery';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { IDictionary } from 'types/Dictionary';
import {
  POSITION_BREAK_SETTINGS_TYPE_INTERVAL,
  POSITION_BREAK_SETTINGS_TYPE_STATIC,
  TPositionBreakSettingsType,
} from 'types/PositionBreakSettings';
import { Nullable } from 'types/util/Nullable';
import { getGraphQlContainsComparison } from 'utils/apolloUtils';
import { MINUTE } from 'utils/constants/timeInterval';
import { positionBreakSettingsFromString } from 'utils/positionBreakSettingsUtils';
import {
  applyAutomaticFixedBreaks,
  applyAutomaticIntervalBreaks,
  TAutomaticBreakPayload,
  TimeEntryRange,
} from 'utils/timeEntryBreakUtils';

export type TUseAutomaticBreaksCreateArgs = TimeEntryRange & {
  memberId: string;
  timeEntryLogId: string;
};

type TPositionBreakSettingsMapItem = {
  breakSeconds: number;
  offsetSeconds: number[];
  type: TPositionBreakSettingsType;
};

export default function useAutomaticBreaksCreate() {
  const { createBreak } = useBreak();
  const timeEntryQuery = useTimeEntryQuery(TIME_ENTRY_AUTOMATIC_BREAK_QUERY);
  const graphqlPaging = useGraphQLPaging();

  return useCallback(
    async (timeEntriesAndLogs: IEntryAndLog[]) => {
      const memberIds = timeEntriesAndLogs.map(({ entry }) => entry.member.id);
      const members = await graphqlPaging<MemberBreakSettingsQuery, MemberBreakSettingsQueryVariables>(
        {
          document: MEMBER_BREAK_SETTINGS_QUERY,
          variables: {
            filter: {
              id: getGraphQlContainsComparison(memberIds),
              deletedOn: { isNull: true },
            },
          },
        },
        'members'
      );

      const memberBreakSettingsMap = members.reduce(
        (acc, member) => {
          const position = member.position;

          if (position.breakSettings) {
            const breakSetting = positionBreakSettingsFromString(position.breakSettings);

            const isAutomaticBreaks =
              breakSetting?.type === POSITION_BREAK_SETTINGS_TYPE_INTERVAL ||
              breakSetting?.type === POSITION_BREAK_SETTINGS_TYPE_STATIC;

            if (isAutomaticBreaks) {
              // TODO: Setting break seconds isn't currently supported and the utilities don't support list of
              // break seconds
              const breakSeconds = breakSetting?.settings?.[0]?.duration ?? 30 * MINUTE;
              const offsetSeconds = breakSetting.settings?.map((setting) => setting.offset).sort() ?? [];
              acc[member.id] = {
                breakSeconds,
                offsetSeconds,
                type: breakSetting.type,
              };
              return acc;
            }
          }

          acc[member.id] = null;
          return acc;
        },
        {} as IDictionary<Nullable<TPositionBreakSettingsMapItem>>
      );

      return timeEntriesAndLogs.map(async (timeEntryAndLog) => {
        const timeEntry = timeEntryAndLog.entry;
        const memberId = timeEntry.member.id;
        const breakSettings = memberBreakSettingsMap[memberId];
        if (breakSettings) {
          const existingTimeEntriesInRange = await timeEntryQuery(
            {
              startTime: DateTime.fromISO(timeEntry.startTime).startOf('day'),
              endTime: DateTime.fromISO(timeEntry.endTime!).endOf('day'),
            },
            false,
            [memberId],
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            200
          );

          const timeEntryRanges = existingTimeEntriesInRange.map((timeEntryInRange) => {
            return {
              id: timeEntryInRange.id,
              startTime: timeEntryInRange.startTime,
              endTime: timeEntryInRange.endTime!,
              breaksInRange: timeEntryInRange.breaks,
            };
          });

          const automaticBreakPayload: TAutomaticBreakPayload = {
            timeEntryRanges,
            breakSeconds: breakSettings.breakSeconds,
            offsetSeconds: breakSettings.offsetSeconds,
          };

          const automaticBreaks =
            breakSettings.type === POSITION_BREAK_SETTINGS_TYPE_INTERVAL
              ? applyAutomaticIntervalBreaks(automaticBreakPayload)
              : applyAutomaticFixedBreaks(automaticBreakPayload);

          return createBreak(
            automaticBreaks.map((automaticBreak) => ({
              timeEntryId: automaticBreak.timeEntryId,
              startTime: DateTime.fromSeconds(automaticBreak.startTime),
              endTime: DateTime.fromSeconds(automaticBreak.endTime),
              entryLogId: timeEntryAndLog.log.id,
            }))
          );
        }
      });
    },
    [createBreak, graphqlPaging, timeEntryQuery]
  );
}

export const TIME_ENTRY_AUTOMATIC_BREAK_QUERY = gql`
  query TimeEntryAutomaticBreaks($after: String, $filter: TimeEntryFilter, $first: Int, $sort: [TimeEntrySort!]) {
    timeEntries(after: $after, filter: $filter, first: $first, sort: $sort) {
      id
      startTime
      endTime
      memberId
      daylightSavingTime
      metaDaylightSavingTime
      actionType
      description
      createdOn
      deletedOn
      submittedOn
      updatedOn
      breaks {
        id
        startTime
        startTimeDst
        endTime
        endTimeDst
        createdOn
        deletedOn
        submittedOn
        updatedOn
      }
      clientLogs {
        id
      }
      cursor
    }
  }
`;

export const MEMBER_BREAK_SETTINGS_QUERY = gql`
  query MemberBreakSettings($first: Int, $after: String, $filter: MemberFilter) {
    members(first: $first, after: $after, filter: $filter) {
      id
      cursor
      position {
        id
        breakSettings
      }
    }
  }
`;
